import { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { useMountEffect } from "@/lib/useMountEffect";

export default function AdminAccount() {
  const [sec, setSec] = useState(null);
  const [error, setError] = useState("");
  const load = () => api.get("/auth/me/security").then((r) => setSec(r.data)).catch((e) => setError(formatApiError(e)));
  useMountEffect(load);

  return (
    <AdminLayout title="My Account" subtitle="Backup email, password recovery and two-step sign-in.">
      {error && <div className="mb-6 text-sm text-destructive" data-testid="account-error">{error}</div>}
      {sec && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
          <BackupEmailCard sec={sec} onSaved={load} />
          <MfaCard sec={sec} onSaved={load} />
        </div>
      )}
    </AdminLayout>
  );
}

function Card({ title, children, testid }) {
  return (
    <section className="border border-border bg-white p-6" data-testid={testid}>
      <h3 className="font-heading text-lg font-semibold tracking-tight mb-4">{title}</h3>
      {children}
    </section>
  );
}

function Input({ label, testid, ...props }) {
  return (
    <label className="block mb-4">
      <span className="block text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">{label}</span>
      <input className="input-swiss" data-testid={testid} {...props} />
    </label>
  );
}

function BackupEmailCard({ sec, onSaved }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async (fn) => {
    setErr(""); setMsg(""); setBusy(true);
    try { await fn(); } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  const send = (e) => { e.preventDefault(); run(async () => {
    const { data } = await api.post("/auth/me/backup-email", { backup_email: email, current_password: password });
    setSentTo(data.sent_to); setPassword("");
  }); };
  const confirm = (e) => { e.preventDefault(); run(async () => {
    await api.post("/auth/me/backup-email/confirm", { code });
    setSentTo(""); setCode(""); setEmail(""); setMsg("Backup email confirmed."); onSaved();
  }); };

  return (
    <Card title="Backup email" testid="account-backup-card">
      <p className="text-sm text-muted-foreground mb-4">
        Used for password reset links and sign-in codes.{" "}
        <span data-testid="account-backup-current">
          {sec.backup_email ? <>Current: <strong className="text-foreground">{sec.backup_email}</strong></> : "Not set."}
        </span>
      </p>
      {sentTo ? (
        <form onSubmit={confirm}>
          <p className="text-sm mb-4">Enter the 6-digit code sent to <strong>{sentTo}</strong>.</p>
          <Input label="Code" testid="account-backup-code" required inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
          <button disabled={busy} className="btn-primary disabled:opacity-60" data-testid="account-backup-confirm">Confirm email</button>
        </form>
      ) : (
        <form onSubmit={send}>
          <Input label={sec.backup_email ? "New backup email" : "Backup email"} testid="account-backup-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input label="Current password" testid="account-backup-password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button disabled={busy} className="btn-primary disabled:opacity-60" data-testid="account-backup-send">Send confirmation code</button>
        </form>
      )}
      {msg && <p className="mt-4 text-sm text-execution" data-testid="account-backup-success">{msg}</p>}
      {err && <p className="mt-4 text-sm text-destructive" data-testid="account-backup-error">{err}</p>}
    </Card>
  );
}

function MfaCard({ sec, onSaved }) {
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const canEnable = sec.backup_email && sec.backup_email_verified;

  useEffect(() => { setPassword(""); }, [sec.mfa_enabled]);

  const toggle = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.put("/auth/me/mfa", { enabled: !sec.mfa_enabled, current_password: password });
      onSaved();
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  return (
    <Card title="Two-step sign-in (MFA)" testid="account-mfa-card">
      <p className="text-sm text-muted-foreground mb-4">
        When on, a 6-digit code is emailed to your backup email each time you sign in.{" "}
        <span className="font-mono text-xs uppercase tracking-wider text-foreground" data-testid="account-mfa-status">
          Status: {sec.mfa_enabled ? "On" : "Off"}
        </span>
      </p>
      {!sec.mfa_enabled && !canEnable ? (
        <p className="text-sm text-muted-foreground" data-testid="account-mfa-needs-backup">Add and confirm a backup email first.</p>
      ) : (
        <form onSubmit={toggle}>
          <Input label="Current password" testid="account-mfa-password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button disabled={busy} className={sec.mfa_enabled ? "btn-secondary disabled:opacity-60" : "btn-primary disabled:opacity-60"} data-testid="account-mfa-toggle">
            {sec.mfa_enabled ? "Turn off MFA" : "Turn on MFA"}
          </button>
        </form>
      )}
      {err && <p className="mt-4 text-sm text-destructive" data-testid="account-mfa-error">{err}</p>}
    </Card>
  );
}
